import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { AutocompleteRenderInputParams } from "@mui/material";
import { TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import type { ClusterRunResponse } from "src/queries/compute/compute.types";
import RunTimeSelectorDefaultRenderOption, {
  type RunTimeSelectorDefaultRenderOptionProps,
  type RunTimeSelectorOptionOnClickType,
} from "./components/RunTimeSelectorDefaultRenderOption/RunTimeSelectorDefaultRenderOption";
import { filterOptions, SLOT_PROPS } from "./RunTimeSelector.constants";
import {
  createGetOptionKey,
  createGetOptionLabel,
  createIsOptionEqualToValue,
  createKeyDownHandler,
  focusInput,
} from "./RunTimeSelector.utils";

export interface RunTimeSelectorProps {
  value?: string;
  options?: ClusterRunResponse[];
  disabled?: boolean;
  onChange?: (pit: string) => void;
  isLoading?: boolean;
  keyName?: string;
  formSx?: any;
  autoFocus?: boolean;
  open?: boolean;
  renderOption?: ComponentType<RunTimeSelectorDefaultRenderOptionProps>;
  placeholder?: ReactNode;
  slotProps?: {
    renderOption?: {
      onClick?: RunTimeSelectorOptionOnClickType;
    };
  };
}

const RunTimeSelector = ({
  value,
  options,
  disabled,
  onChange,
  isLoading,
  formSx,
  open,
  autoFocus,
  renderOption = RunTimeSelectorDefaultRenderOption,
  placeholder = "Select run",
  slotProps,
  keyName = "run_time",
  ...props
}: RunTimeSelectorProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const RenderOption = renderOption;

  const localSlotProps = useMemo(
    () => ({ ...SLOT_PROPS, ...slotProps }),
    [slotProps]
  );
  const isOptionEqualToValue = useMemo(
    () => createIsOptionEqualToValue(keyName),
    [keyName]
  );
  const getOptionKey = useMemo(() => createGetOptionKey(keyName), [keyName]);
  const getOptionLabel = useMemo(() => createGetOptionLabel(keyName), [keyName]);
  const onKeyDown = useMemo(() => createKeyDownHandler(), []);

  const onLocalChange = useCallback(
    (_e: any, value: any) => {
      onChange?.(value?.[keyName] || "");
    },
    [onChange, keyName]
  );

  // Handle autoFocus without controlling the open state
  useEffect(() => {
    return focusInput(inputRef, autoFocus || false, isLoading || false);
  }, [autoFocus, isLoading]);

  return (
    <Autocomplete<ClusterRunResponse, boolean, boolean, boolean>
      loading={isLoading}
      value={value}
      openOnFocus
      open={open}
      autoSelect={true}
      onKeyDown={onKeyDown}
      onChange={onLocalChange}
      disabled={isLoading || disabled}
      size="small"
      options={options || []}
      filterOptions={filterOptions}
      includeInputInList={true}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={isLoading ? "Fetching runs…" : placeholder}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <RenderOption
            key={key}
            option={option}
            {...optionProps}
            onClick={
              slotProps?.renderOption?.onClick ||
              (optionProps.onClick as RunTimeSelectorOptionOnClickType)
            }
          />
        );
      }}
      slotProps={localSlotProps}
      data-test-id="run-time-selector"
      {...props}
    />
  );
};

export default RunTimeSelector;
